import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Onboarding 2.0 draft values — what the user typed/picked on each data-collection
 * step, persisted to localStorage so navigating Back (or reloading mid-flow)
 * shows the previous entry instead of an empty field.
 *
 * This is a client-side prefill cache, NOT the source of truth. Steps still
 * persist to the backend where an endpoint exists (category → setPrimaryCategory;
 * firstName / storeUrl / platform pending backend fields — see
 * docs/onboarding-2.0-backend-contract.md). Cleared on registration completion so
 * a later signup on the same browser doesn't inherit a stale draft.
 */
interface OnboardingDraftState {
  firstName: string;
  ecommercePlatform: string;
  storeUrl: string;
  primaryCategory: string;
  /** The 4-step redesign's business step: the merchant's company name. */
  companyName: string;
  /**
   * The 4-step redesign's business step: "What do you sell online?" is now MULTI-select
   * ("Select all that apply"). The first entry is still persisted as the Brand IQ primary
   * category (setPrimaryCategory), which stays single; this array is the full selection.
   */
  categories: string[];
}

interface OnboardingDraftStore extends OnboardingDraftState {
  setFirstName: (v: string) => void;
  setEcommercePlatform: (v: string) => void;
  setStoreUrl: (v: string) => void;
  setPrimaryCategory: (v: string) => void;
  setCompanyName: (v: string) => void;
  setCategories: (v: string[]) => void;
  reset: () => void;
}

const EMPTY: OnboardingDraftState = {
  firstName: '',
  ecommercePlatform: '',
  storeUrl: '',
  primaryCategory: '',
  companyName: '',
  categories: [],
};

export const useOnboardingDraftStore = create<OnboardingDraftStore>()(
  persist(
    set => ({
      ...EMPTY,
      setFirstName: firstName => set({ firstName }),
      setEcommercePlatform: ecommercePlatform => set({ ecommercePlatform }),
      setStoreUrl: storeUrl => set({ storeUrl }),
      setPrimaryCategory: primaryCategory => set({ primaryCategory }),
      setCompanyName: companyName => set({ companyName }),
      setCategories: categories => set({ categories }),
      reset: () => set({ ...EMPTY }),
    }),
    { name: 'onboarding-draft' }
  )
);
